"use client";

import { Button } from "@/components/ui/button";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  COOKIE_CONSENT_EVENT,
  CookieConsentChoice,
  clearOptionalCookies,
  persistCookieConsent,
  readCookieConsent,
} from "./consent";

const GTM_SCRIPT_ID = "gs-consented-gtm";
const GTM_CONTAINER_ID = "GTM-5VD2XD3V";
export const COOKIE_SETTINGS_WIDGET_GUARD_ID =
  "gs-cookie-settings-widget-guard";

const COOKIE_SETTINGS_WIDGET_GUARD_CSS = `
#trustedShopsCheckout,
[id*="trustedshops" i],
[id*="trusted-shops" i],
[id*="trustbadge" i],
[id*="etrusted" i],
[class*="trustedshops" i],
[class*="trusted-shops" i],
[class*="trustbadge" i],
[class*="etrusted" i],
iframe[src*="trustedshops" i],
iframe[src*="trusted-shops" i],
iframe[src*="trustbadge" i],
iframe[src*="etrusted" i] {
  display: none !important;
  pointer-events: none !important;
  visibility: hidden !important;
}
`;

const COPY = {
  de: {
    label: "Cookie-Einstellungen",
    title: "Ihre Privatsphäre ist uns wichtig",
    description:
      "Notwendige Cookies sorgen dafür, dass diese Website funktioniert. Mit Ihrer Zustimmung verwenden wir außerdem Google Tag Manager und Trusted Shops, um Reichweite und Nutzung zu messen.",
    necessary: "Nur notwendige Cookies",
    all: "Alle akzeptieren",
    policy: "Cookie-Richtlinie",
    privacy: "Datenschutzerklärung",
  },
  en: {
    label: "Cookie settings",
    title: "Your privacy matters to us",
    description:
      "Necessary cookies keep this website working. With your consent, we also use Google Tag Manager and Trusted Shops to measure reach and usage.",
    necessary: "Necessary cookies only",
    all: "Accept all",
    policy: "Cookie policy",
    privacy: "Privacy policy",
  },
} as const;

type CookieConsentContextValue = {
  choice: CookieConsentChoice | null;
  ready: boolean;
  openSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue>({
  choice: null,
  ready: false,
  openSettings: () => undefined,
});

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function loadGoogleTagManager() {
  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": Date.now(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
}

export function CookieConsentProvider({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale: string;
}) {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(null);
  const [ready, setReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const copy = locale === "en" ? COPY.en : COPY.de;
  const localePrefix = locale === "en" ? "/en" : "";
  const bannerOpen = ready && (choice === null || settingsOpen);

  useEffect(() => {
    const storedChoice = readCookieConsent();
    setChoice(storedChoice);
    setReady(true);

    if (storedChoice !== "all") {
      clearOptionalCookies();
    }
  }, []);

  useEffect(() => {
    if (choice === "all") {
      loadGoogleTagManager();
    }
  }, [choice]);

  const choose = useCallback(
    (nextChoice: CookieConsentChoice) => {
      const mustReload = choice === "all" && nextChoice === "necessary";
      persistCookieConsent(nextChoice);
      setChoice(nextChoice);
      setSettingsOpen(false);

      if (nextChoice === "necessary") {
        clearOptionalCookies();
      }

      window.dispatchEvent(
        new CustomEvent(COOKIE_CONSENT_EVENT, { detail: nextChoice }),
      );

      if (mustReload) {
        window.location.reload();
      }
    },
    [choice],
  );

  const value = useMemo(
    () => ({
      choice,
      ready,
      openSettings: () => setSettingsOpen(true),
    }),
    [choice, ready],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}

      {bannerOpen ? (
        <style id={COOKIE_SETTINGS_WIDGET_GUARD_ID}>
          {COOKIE_SETTINGS_WIDGET_GUARD_CSS}
        </style>
      ) : null}

      {bannerOpen ? (
        <section
          role="dialog"
          aria-modal="false"
          aria-label={copy.label}
          className="fixed right-4 bottom-4 left-4 z-[100] mx-auto max-w-5xl rounded-lg border border-gray-200 bg-white p-5 shadow-2xl sm:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <h2 className="text-primary text-lg font-bold sm:text-xl">
                {copy.title}
              </h2>
              <p className="mt-2 text-sm leading-6 text-gray-700">
                {copy.description}
              </p>
              <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <a
                  className="text-primary underline underline-offset-4"
                  href={`${localePrefix}/cookie-richtlinie`}
                >
                  {copy.policy}
                </a>
                <a
                  className="text-primary underline underline-offset-4"
                  href={`${localePrefix}/datenschutz`}
                >
                  {copy.privacy}
                </a>
              </p>
            </div>
            <div className="grid shrink-0 gap-3 sm:grid-cols-2 lg:min-w-[380px]">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="h-auto min-h-11 whitespace-normal"
                onClick={() => choose("necessary")}
              >
                {copy.necessary}
              </Button>
              <Button
                type="button"
                size="lg"
                className="h-auto min-h-11 whitespace-normal"
                onClick={() => choose("all")}
              >
                {copy.all}
              </Button>
            </div>
          </div>
        </section>
      ) : null}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
